import { Message } from '@/types/message-types';

export interface AggregatedReaction {
  emoji: string;
  count: number;
  senderIds: string[];
}

export interface MessageWithReactions {
  message: Message;
  reactions: AggregatedReaction[];
}

export function aggregateReactions(messages: Message[]): {
  messagesWithReactions: MessageWithReactions[];
  reactionMessageIds: Set<string>;
} {
  const reactionMap = new Map<string, Map<string, Set<string>>>();
  const reactionMessageIds = new Set<string>();
  
  console.log('🎯 REACTION DEBUG: Starting aggregation with', messages.length, 'messages');
  
  // First pass: collect all reactions
  messages.forEach(message => {
    if (message.message_type === 'reaction') {
      console.log('🎯 REACTION FOUND:', {
        messageId: message.id,
        senderId: message.sender_id,
        content: message.content,
        metadata: message.metadata,
        metadataType: typeof message.metadata
      });
      
      // Parse metadata if it's a string
      let metadata = message.metadata;
      if (typeof metadata === 'string') {
        try {
          metadata = JSON.parse(metadata);
          console.log('🎯 PARSED METADATA:', metadata);
        } catch (e) {
          console.warn('Failed to parse reaction metadata:', e);
          return;
        }
      }
      
      const originalWhatsappMessageId = metadata?.original_whatsapp_message_id;
      const emoji = metadata?.reaction_emoji;
      const senderId = message.sender_id || 'customer'; // Default to 'customer' if sender_id is null
      
      console.log('🎯 REACTION DATA:', {
        originalWhatsappMessageId,
        emoji,
        senderId
      });
      
      if (originalWhatsappMessageId && emoji && senderId) {
        // Find the target message by matching external_id
        const targetMessage = messages.find(m => m.external_id === originalWhatsappMessageId);
        const targetMessageId = targetMessage?.id;
        
        console.log('🎯 TARGET MESSAGE SEARCH:', {
          originalWhatsappMessageId,
          targetMessage: targetMessage ? {
            id: targetMessage.id,
            external_id: targetMessage.external_id,
            content: targetMessage.content?.substring(0, 50) + '...'
          } : null,
          targetMessageId
        });
        
        if (targetMessageId) {
          console.log('🎯 ADDING REACTION to message:', targetMessageId);
          reactionMessageIds.add(message.id);
          
          if (!reactionMap.has(targetMessageId)) {
            reactionMap.set(targetMessageId, new Map());
          }
          
          const messageReactions = reactionMap.get(targetMessageId)!;
          if (!messageReactions.has(emoji)) {
            messageReactions.set(emoji, new Set());
          }
          
          messageReactions.get(emoji)!.add(senderId);
        } else {
          console.warn('🎯 TARGET MESSAGE NOT FOUND for originalWhatsappMessageId:', originalWhatsappMessageId);
        }
      } else {
        console.warn('🎯 MISSING REACTION DATA:', { originalWhatsappMessageId, emoji, senderId });
      }
    }
  });
  
  // Second pass: create messages with aggregated reactions
  const messagesWithReactions: MessageWithReactions[] = [];
  
  messages.forEach(message => {
    // Skip standalone reaction messages
    if (message.message_type === 'reaction') {
      return;
    }
    
    const reactions: AggregatedReaction[] = [];
    const messageReactions = reactionMap.get(message.id);
    
    if (messageReactions) {
      messageReactions.forEach((senderIds, emoji) => {
        reactions.push({
          emoji,
          count: senderIds.size,
          senderIds: Array.from(senderIds)
        });
      });
      
      // Sort reactions by count (most popular first)
      reactions.sort((a, b) => b.count - a.count);
    }
    
    messagesWithReactions.push({
      message,
      reactions
    });
  });
  
  return { messagesWithReactions, reactionMessageIds };
}